import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useDropzone } from 'react-dropzone'
import { getData, formatFormData, formatSlug } from 'common/utils'
import { HOSTNAME } from 'constant/appConstant'
import { Row, Col } from 'components/ui'
import { Editor } from '@tinymce/tinymce-react'
import { Input } from 'antd'
import AlertDialog from 'components/ui/AlertDialog'
import TextField from 'components/form/TextField'
import Checkbox from 'components/form/Checkbox'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import DeleteIcon from '@mui/icons-material/Delete'
import Autocomplete from 'components/form/Autocomplete'
import AutocompleteMultiple from 'components/form/AutocompleteMultiple'
import AutocompleteMultipleTagTopic from 'components/form/AutocompleteMultipleTagTopic'
import DateTimePicker from 'components/form/DateTimePicker'
import Button from 'components/form/Button'
import FormDialog from 'components/form/FormDialog'

const { TextArea } = Input

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
}

const focusedStyle = {
  borderColor: '#2196f3'
}

const acceptStyle = {
  borderColor: '#00e676'
}

const rejectStyle = {
  borderColor: '#ff1744'
}

const VodMp3 = (props) => {
  const {
    id,
    control,
    errors,
    setValue,
    type,
    statusList, peopleTypeList, peopleList, tagTopicList, hashtagList, countryCodeList,
    longDescription, editorRef, searchTagTopic, onSearchTagTopic, onSourceId, onMp3Files
  } = props

  const { handleSubmit: handleSubmitPeopleType, control: controlPeopleType, formState: { errors: errorsPeopleType }, setError: setErrorPeopleType } = useForm()
  const queryClient = useQueryClient()
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    accept: 'audio/mp3, audio/mpeg',
    onDrop: acceptedFiles => {
      onMp3Files(acceptedFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      })))
      setFiles(acceptedFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      })))
    }
  })

  const { data: dataListEntityVoice } = useQuery(['getListEntityVoice', id], async () => {
    if (id) {
      return await getData('GET', `vod-entities/${id}/entity-voices`)
    }
  })

  const [isOpenPeopleType, setIsOpenPeopleType] = useState(false)
  const [hashtags, setHashTags] = useState('')
  // eslint-disable-next-line no-unused-vars
  const [files, setFiles] = useState([])
  const [mp3List, setMp3List] = useState([])

  const [idDelete, setIdDelete] = useState(null)
  const [isOpenDelete, setIsOpenDelete] = useState(false)

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isFocused ? focusedStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isFocused,
    isDragAccept,
    isDragReject
  ])

  useEffect(() => {
    if (hashtagList) {
      setHashTags(hashtagList)
    }
  }, [hashtagList])

  const mutation = useMutation(async (dataForm) => {
    if (dataForm) {
      return await getData('POST', 'people-types/', dataForm)
    }
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries('getVodPeopleTypes')
    }
  })

  const mutationDelete = useMutation(async (id) => {
    if (id) {
      return await getData('DELETE', `entity_voice/${id}/`)
    }
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries('getListEntityVoice')
    }
  })

  useEffect(() => {
    if (dataListEntityVoice) {
      const resultData = dataListEntityVoice.data
      if (resultData) {
        setMp3List(resultData.results)
      }
    }
  }, [dataListEntityVoice])

  /* ----------------------------------------------- */
  /* --------------- Handle Event ------------------ */
  /* ----------------------------------------------- */
  const onPeopleTypeSubmit = async (data) => {
    try {
      await mutation.mutateAsync({
        name: formatFormData(data.name),
        tenant: localStorage.getItem('tenant')
      })
      setIsOpenPeopleType(false)
    } catch (err) {
      if (err.response && err.response.status === 400) {
        const errData = err.response.data && err.response.data.errors
        if (typeof errData === 'object') {
          for (const item in errData) {
            const errItemArr = errData[item]
            setErrorPeopleType(item, {
              type: 'manual',
              message: Array.isArray(errItemArr) && errItemArr[0]
            })
          }
        }
      }
      console.log(err)
    }
  }

  const handleClickShowPeopleType = () => {
    setIsOpenPeopleType(true)
  }

  const handleClosePeopleType = () => {
    setIsOpenPeopleType(false)
  }

  const handleChangeTitle = (e) => {
    if (e && e.target) {
      if (setValue) {
        setValue('slug', formatSlug(e.target.value))
      }
    }
  }

  const handleChangeHashTag = (e) => {
    if (e && e.target) {
      if (setValue) {
        setHashTags(e.target.value)
        setValue('hashtags', e.target.value)
      }
    }
  }

  const handleInputChangeTagTopic = (e) => {
    onSearchTagTopic(e?.target?.value)
  }

  const handleChangeSourceId = (e) => {
    onSourceId(e?.target?.value)
  }

  const handleClickShowModalDelete = (e, record) => {
    setIdDelete(record?.id)
    setIsOpenDelete(true)
  }
  const handleCloseDelete = () => {
    setIsOpenDelete(false)
  }

  const handleClickDelete = async () => {
    try {
      await mutationDelete.mutateAsync(idDelete)

      setIsOpenDelete(false)
    } catch (err) {
      setIsOpenDelete(false)
      console.log(err)
    }
  }

  const handleClickDeleteFile = () => {
    setFiles([])
  }

  return (
    <div className="vod-singlemovie-form">
      <Row className="pt-4">
        <Col md={5}>
          <TextField type="number" label="Rating (Voted)" name="rating" disabled control={control} error={errors.rating} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField
            label="Title"
            name="title"
            required
            // inputProps={{
            //   maxLength: window.location.hostname === HOSTNAME.VTCNOW || window.location.hostname === HOSTNAME.VTCNOW_STAGING ? 60 : 524288
            // }}
            control={control}
            error={errors.title}
            onHandleChange={handleChangeTitle}
          />
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <TextField label="English name" name="known_as" control={control} error={errors.known_as} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField label="Slug" name="slug" required control={control} error={errors.slug} />
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <TextField label="Short description" name="short_description" control={control} error={errors.short_description} />
        </Col>
        <Col md={5} className="offset-md-2">
          <DateTimePicker label="Release Date" name="release_date" control={control} />
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <DateTimePicker label="Unrelease Date" name="unrelease_date" control={control} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField type="number" label="Release Year" name="release_year" control={control} error={errors.release_year} />
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <DateTimePicker label="Schedule publish" name="schedule_date" control={control} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField label="Link Play" name="link_play" control={control} error={errors.link_play} />
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <TextField label="SourceID Play" name="source_id" control={control} error={errors.source_id} onHandleChange={handleChangeSourceId} />
        </Col>
        <Col md={5} className="offset-md-2">
          {(window.location.hostname === HOSTNAME.VTCNOW || window.location.hostname === HOSTNAME.VTCNOW_STAGING || window.location.hostname === HOSTNAME.DEMO)
            ? <Autocomplete
                options={statusList}
                label="Status"
                name="status"
                control={control}
                error={errors.status}
                disabled={!id}
              />
            : <Autocomplete
                options={statusList}
                label="Status"
                name="status"
                control={control}
                error={errors.status}
              />
          }
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <TextField type="number" label="Runtime" name="runtime" control={control} error={errors.runtime} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField type="number" label="Total Season" name="total_seasons" control={control} error={errors.total_seasons} />
        </Col>
      </Row>
      <Row className="pt-4">
        <Col md={5}>
          <TextField type="number" label="Views (Real)" name="real_views" control={control} error={errors.real_views} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField type="number" label="Views (Fake)" name="views" control={control} error={errors.views} />
        </Col>
      </Row>

      {(window.location.hostname === HOSTNAME.BRT || window.location.hostname === HOSTNAME.SDTV) &&
        <Row className="pt-4">
          <Col md={5}>
            <TextField type="number" label="Duration in minutes" name="duration_minute" control={control} error={errors.duration_minute} />
          </Col>
          <Col md={5} className="offset-md-2">
            <TextField type="number" label="Duration in seconds" name="duration_second" control={control} error={errors.duration_second} />
          </Col>
        </Row>
      }

      <Row className="pt-4">
        <Col md={5}>
          <TextField type="number" label="Favorites" name="favorites" control={control} error={errors.favorites} />
        </Col>
        <Col md={5} className="offset-md-2">
          <TextField type="number" label="Intro time" name="intro_time" control={control} error={errors.intro_time} />
        </Col>
      </Row>

      <Row className="pt-4">
        <Col md={5}>
          <TextField label="Reference Link" name="ref_link" control={control} error={errors.ref_link} />
        </Col>

        <Col md={5} className="offset-md-2">
          <div className="pb-1">Insert file audio</div>
          <section>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p>Drag and drop some files here, or click to select files</p>
            </div>
          </section>

          <div>
            {Array.isArray(files) && files.map((item, index) => {
              return (
                <div key={index} className='mt-3' style={{ display: 'flex', alignItems: 'center' }}>
                  <div>
                    <AudioFileIcon />
                  </div>
                  <div className='ml-1' style={{ flexGrow: 1 }}>
                    <TextField label="Name" name="img_name" control={control} error={errors.img_name} />
                  </div>
                  <div onClick={handleClickDeleteFile}>
                    <DeleteIcon color='error' style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              )
            })}
          </div>

          <div>
            {Array.isArray(mp3List) && mp3List.map((item, index) => {
              return (
                <div key={index} className='mt-3' style={{ display: 'flex' }}>
                  <div>
                    <AudioFileIcon />
                  </div>
                  <div className='ml-1' style={{ flexGrow: 1 }}>{item?.title}</div>
                  <div onClick={e => handleClickShowModalDelete(e, item)}>
                    <DeleteIcon color='error' style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </Col>
      </Row>

      <Row className="pt-4">
        <Col md={12}>
          <Checkbox name="released" className="d-inline-block" control={control} label="Realeased State" />
          <Checkbox name="is_vip" className="pt-4 ml-4 d-inline-block" control={control} label="Require VIP" />
          <Checkbox name="is_drm" className="pt-3 ml-4 d-inline-block" control={control} label="VOD DRM" />
          <Checkbox name="is_samsung_preview" className="pt-3 ml-4 d-inline-block" control={control} label="Samsung Preview" />
          <Checkbox name="geo_check" className="pt-3 ml-4 d-inline-block" control={control} label="GEOIP Check" />
          {(window.location.hostname === HOSTNAME.VTCNOW || window.location.hostname === HOSTNAME.VTCNOW_STAGING || window.location.hostname === HOSTNAME.DEMO) &&
            type === 15 &&
            <Checkbox name="is_live" className="pt-3 ml-4 d-inline-block" control={control} label="Live" />
          }
          {(window.location.hostname === HOSTNAME.LOCALHOST || window.location.hostname === HOSTNAME.VTCNOW || window.location.hostname === HOSTNAME.VTCNOW_STAGING || window.location.hostname === HOSTNAME.DEMO) &&
            <Checkbox name="is_autoplay" className="pt-3 ml-4 d-inline-block" control={control} label="Autoplay" />
          }
        </Col>
      </Row>
      {peopleTypeList && Array.isArray(peopleTypeList) && peopleTypeList.map((item, index) => {
        return (
          <Row className="pt-4" key={index}>
            <Col md={12}>
              <AutocompleteMultiple
                options={peopleList}
                label={item.name}
                name={`people_types_${index}`}
                control={control}
                error={errors.people_types}
              />
            </Col>
          </Row>
        )
      })}
      <Row className="pt-3">
        <Col md={12}>
          <Button className="d-inline-block" onClick={handleClickShowPeopleType}>Add People Type</Button>
        </Col>
      </Row>

      {(window.location.hostname === HOSTNAME.VTCNOW || window.location.hostname === HOSTNAME.VTCNOW_STAGING || window.location.hostname === HOSTNAME.DEMO) &&
        <Row className='pt-5'>
          <Col md={12}>
            {/* <div className="description-title">Tag Topic</div> */}

            <Row>
              <Col md={12}>
                <AutocompleteMultipleTagTopic
                  options={tagTopicList}
                  label="Tag Topic"
                  name="tags"
                  control={control}
                  error={errors.tags}
                  inputValue={searchTagTopic}
                  freeSolo
                  onInputChange={handleInputChangeTagTopic}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      }

      <Row className='pt-5'>
        <Col md={12}>
          {/* <TextField type="textarea" label="Info" name="long_description" control={control} error={errors.long_description} /> */}
          <div className="description-title">Hashtags</div>
          <TextArea
            placeholder="Hashtag split by comma ( Ex: Hashtag1, Hashtag2,... )"
            autoSize={{
              minRows: 3,
              maxRows: 8
            }}
            value={hashtags}
            onChange={(e) => handleChangeHashTag(e)}
          />
        </Col>
      </Row>

      {(window.location.hostname === HOSTNAME.VTCNOW || window.location.hostname === HOSTNAME.VTCNOW_STAGING || window.location.hostname === HOSTNAME.LOCALHOST) &&
        <Row className='pt-5'>
          <Col md={12}>
            <div className="description-title">GEO Blocking</div>

            <Row className='pt-2'>
              <Col md={12}>
                <AutocompleteMultiple
                  options={countryCodeList}
                  label="Blacklist"
                  name="iso_code_blacklist"
                  control={control}
                  error={errors.iso_code_blacklist}
                />
              </Col>
            </Row>

            <Row className='pt-4'>
              <Col md={12}>
                <AutocompleteMultiple
                  options={countryCodeList}
                  label="Whitelist"
                  name="iso_code_whitelist"
                  control={control}
                  error={errors.iso_code_whitelist}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      }

      <Row className="pt-5">
        <Col md={12}>
          {/* <TextField type="textarea" label="Info" name="long_description" control={control} error={errors.long_description} /> */}
          <div className="description-title">Description</div>
          <Editor
            apiKey='8xwdiofw91cl5ikp80dpkij70sj7u8uziroarh8v1420aq6y'
            tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
            // eslint-disable-next-line no-return-assign
            onInit={(evt, editor) => editorRef.current = editor}
            initialValue={longDescription}
            init={{
              height: 350,
              branding: false,
              // plugins: [
              //   'advlist autolink lists link charmap preview anchor',
              //   'searchreplace visualblocks code fullscreen',
              //   'insertdatetime table paste code help wordcount'
              // ]
              plugins: 'advlist autolink lists link charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime table code help wordcount',
              toolbar: 'undo redo | formatselect | ' +
                'bold italic backcolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | help',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              file_picker_callback: (cb, value, meta) => {
                const input = document.createElement('input')
                input.setAttribute('type', 'file')
                input.setAttribute('accept', 'application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.presentationml.slideshow, text/plain, application/pdf, image/*')
                input.onchange = function () {
                  const file = this.files[0]
                  const reader = new FileReader()

                  // FormData
                  const fd = new FormData()
                  const files = file
                  let filename = ''
                  // AJAX
                  let xhr = null
                  xhr = new XMLHttpRequest()
                  xhr.withCredentials = false

                  if (file.name.match(/\.(jpg|jpeg|png|gif)$/i)) {
                    // fd.append('type', '25f572f5-3447-4bb5-8ff9-483aadf72258')
                    fd.append('image', files)
                    fd.append('tenant', localStorage.getItem('tenant'))
                    xhr.open('POST', `${process.env.REACT_APP_API_URL}images/`)
                  } else {
                    fd.append('file', files)
                    xhr.open('POST', `${process.env.REACT_APP_API_URL}tenants/${localStorage.getItem('tenant')}/upload_file/`)
                  }

                  xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`)
                  xhr.onload = function () {
                    let json = null
                    if (xhr.status >= 300) {
                      alert('HTTP Error: ' + xhr.status)
                      return
                    }
                    json = JSON.parse(xhr.responseText)
                    if (!json) {
                      alert('Invalid JSON: ' + xhr.responseText)
                      return
                    }
                    filename = json.data?.url
                    reader.onload = function (e) {
                      cb(filename)
                    }
                    reader.readAsDataURL(file)
                  }
                  xhr.send(fd)
                }

                input.click()
              }
            }}
          />
        </Col>
      </Row>

      <FormDialog
        isOpen={isOpenPeopleType}
        title="Add People Type"
        onClose={handleClosePeopleType}
        onSave={handleSubmitPeopleType(onPeopleTypeSubmit)}
      >
        <Row>
          <Col md={12}>
            <TextField label="Name" name="name" required control={controlPeopleType} error={errorsPeopleType.name} />
          </Col>
        </Row>
      </FormDialog>

      <AlertDialog
        isOpen={isOpenDelete}
        type="delete"
        title="Notification"
        contentText="This action cannot be undone. Are you sure you want to delete this item?"
        onClose={handleCloseDelete}
        onSave={handleClickDelete}
      />
    </div>
  )
}

VodMp3.propTypes = {
  id: PropTypes.any,
  control: PropTypes.any,
  errors: PropTypes.any,
  setValue: PropTypes.any,
  type: PropTypes.number,
  statusList: PropTypes.array,
  peopleTypeList: PropTypes.array,
  peopleList: PropTypes.array,
  tagTopicList: PropTypes.array,
  longDescription: PropTypes.string,
  hashtagList: PropTypes.string,
  countryCodeList: PropTypes.array,
  editorRef: PropTypes.any,
  searchTagTopic: PropTypes.any,
  onSearchTagTopic: PropTypes.func,
  onSourceId: PropTypes.func,
  onMp3Files: PropTypes.func
}

VodMp3.defaultProps = {
  id: null,
  type: 0,
  statusList: [],
  peopleTypeList: [],
  peopleList: [],
  tagTopicList: [],
  countryCodeList: [],
  longDescription: '',
  hashtagList: ''
}

export default VodMp3
